import { useDialog } from "hooks/useDialog.ts";
import { useNavigate } from "react-router";
import { StyledDialog } from "components/Impersonation/impersonation.styles.ts";
import { Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
	DeclineLeadCancelButtonText,
	DeclineLeadDeclineLead,
	DeclineLeadInfoBannerTitle,
	DeclineLeadModalTitle,
	DeclineReasonBrandNotSoldHere,
	DeclineReasonInsufficientStaff,
	DeclineReasonIssueWithLead,
	DeclineReasonJobSizeTooBig,
	DeclineReasonJobSizeTooSmall,
	DeclineReasonLabel,
	DeclineReasonNoRoomInCurrentSchedule,
	DeclineReasonOutOfMarket
} from "../../LeadDetails.text.ts";
import React, { useState } from "react";
import InfoBanner from "components/Common/InfoBanner/InfoBanner.tsx";
import {
	DeclineLeadButton,
	DeclineLeadModalTitleContainer,
	StyledDialogActions,
	StyledDialogContent
} from "./DeclineLeadModal.styles.ts";
import AutoComplete from "components/Common/Autocomplete/Autocomplete.tsx";
import { useUpdateLeadAssignmentMutation } from "features/LeadGen/api";

interface DeclineLeadModalProps {
	assignmentId: string;
	leadId: string;
	status: string;
	notes: string;
}

const DeclineLeadModal = ({ assignmentId, leadId, status, notes }: DeclineLeadModalProps) => {
	const { closeDialog } = useDialog();

	const [updateLeadAssignments, { isError: _isUpdateError }] = useUpdateLeadAssignmentMutation();

	const navigate = useNavigate();

	const declineLeadReasons = [
		DeclineReasonOutOfMarket,
		DeclineReasonJobSizeTooSmall,
		DeclineReasonJobSizeTooBig,
		DeclineReasonInsufficientStaff,
		DeclineReasonNoRoomInCurrentSchedule,
		DeclineReasonBrandNotSoldHere,
		DeclineReasonIssueWithLead
	];

	const [reason, setReason] = useState(declineLeadReasons[0]);

	const handleCancel = () => {
		closeDialog();
	};

	const declineLead = async () => {
		const body = {
			Status: status,
			LeadAssignmentNotes: notes,
			DeclineReason: reason
		};

		await updateLeadAssignments({ leadId, assignmentId, request: body }).unwrap();

		setTimeout(() => {
			navigate("/lead-assignments");
			closeDialog();
		}, 1000);
	};

	return (
		<StyledDialog
			open={true}
			onClose={handleCancel}
			maxWidth="md"
			fullWidth
			data-testid="decline-lead-modal"
		>
			<DeclineLeadModalTitleContainer>
				<h6>{DeclineLeadModalTitle}</h6>
				<IconButton
					onClick={handleCancel}
					data-testid="close-modal-button"
				>
					<CloseIcon />
				</IconButton>
			</DeclineLeadModalTitleContainer>
			<StyledDialogContent>
				<InfoBanner title={DeclineLeadInfoBannerTitle} />
				<AutoComplete
					options={declineLeadReasons}
					getOptionLabel={(option) => option}
					onChange={(_event, newReason) => {
						setReason(newReason);
					}}
					value={reason}
					isError={false}
					errorText={undefined}
					label={DeclineReasonLabel}
					dataTestId="lead-details-lead-status"
					disableClearable
					required
				/>
				<StyledDialogActions>
					<Button
						onClick={handleCancel}
						variant="outlined"
						data-testid="decline-lead-modal-cancel-button"
					>
						{DeclineLeadCancelButtonText}
					</Button>
					<DeclineLeadButton
						onClick={declineLead}
						variant="contained"
						data-testid="decline-lead-modal-decline-button"
					>
						{DeclineLeadDeclineLead}
					</DeclineLeadButton>
				</StyledDialogActions>
			</StyledDialogContent>
		</StyledDialog>
	);
};

export default DeclineLeadModal;
